import React, { useEffect, useState } from "react";
import CommonApiService from "../../ApiServices/CommonApiService";
import { useAuth } from "../Auth/AuthContext";
import { Hash, FileText, AlignLeft, CheckCircle, Plus, X, Save } from "lucide-react";
import DataTable from 'react-data-table-component';
import 'react-data-table-component-extensions/dist/index.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function Notes() {
    const { authState } = useAuth();
    const role = authState.roles;
    const isAdmin = role === "Admin";

    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newNote, setNewNote] = useState({ title: "", description: "" });
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);

    useEffect(() => {
        fetchNotes();
    }, []);

    const fetchNotes = async () => {
        try {
            setLoading(true);
            const data = await CommonApiService.getAllNotes();
            setNotes(data);
            setError(null);
        } catch (err) {
            setError(err.message);
            toast.error(`Error fetching notes: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleAddNote = async () => {
        if (!newNote.title.trim()) {
            toast.error("Title is required");
            return;
        }
        if (!newNote.description.trim()) {
            toast.error("Description is required");
            return;
        }

        try {
            await CommonApiService.createNote(newNote);
            toast.success("Note added successfully!");
            setIsModalOpen(false);
            setNewNote({ title: "", description: "" });
            fetchNotes();
        } catch (error) {
            console.error("Error adding note:", error);
            toast.error(`Error adding note: ${error.message}`);
        }
    };

    const handleDelete = async () => {
        if (selectedRows.length === 0) return;
        
        try {
            const ids = selectedRows.map(row => row.id);
            await CommonApiService.deleteNotes(ids);
            toast.success(`${ids.length} note(s) deleted successfully!`);
            setToggleCleared(!toggleCleared);
            fetchNotes();
        } catch (error) {
            console.error("Error deleting notes:", error);
            toast.error(`Error deleting notes: ${error.message}`);
        }
    };

    const columns = [
        {
            name: (
                <div className="flex items-center gap-2 text-gray-700">
                    <Hash size={16} />
                    ID
                </div>
            ),
            selector: row => row.id,
            sortable: true,
            width: "80px"
        },
        {
            name: (
                <div className="flex items-center gap-2 text-gray-700">
                    <FileText size={16} />
                    Title
                </div>
            ),
            selector: row => row.title,
            sortable: true,
            cell: row => <span className="font-medium">{row.title}</span>
        },
        {
            name: (
                <div className="flex items-center gap-2 text-gray-700">
                    <AlignLeft size={16} />
                    Description
                </div>
            ),
            selector: row => row.description,
            sortable: true,
            cell: row => <span className="text-gray-600">{row.description}</span>
        },
        {
            name: (
                <div className="flex items-center gap-2 text-gray-700">
                    <CheckCircle size={16} />
                    Status
                </div>
            ),
            selector: row => row.isActive,
            sortable: true,
            cell: row => (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    row.isActive 
                        ? "bg-green-100 text-green-800" 
                        : "bg-red-100 text-red-800"
                }`}>
                    {row.isActive ? "Active" : "Inactive"}
                </span>
            ),
            width: "100px"
        }
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#f8fafc',
                borderBottomWidth: '1px',
                borderColor: '#e2e8f0',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.025em',
            },
        },
        headCells: {
            style: {
                paddingLeft: '1rem',
                paddingRight: '1rem',
                paddingTop: '0.75rem',
                paddingBottom: '0.75rem',
                color: '#64748b',
            },
        },
        cells: {
            style: {
                paddingLeft: '1rem',
                paddingRight: '1rem',
                paddingTop: '1rem',
                paddingBottom: '1rem',
            },
        },
        rows: {
            style: {
                '&:not(:last-of-type)': {
                    borderBottomWidth: '1px',
                    borderColor: '#e2e8f0',
                },
                '&:hover': {
                    backgroundColor: '#f8fafc',
                },
            },
        },
    };

    const contextActions = (
        <button 
            onClick={handleDelete}
            className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm"
        >
            <X size={16} />
            Delete {selectedRows.length} note(s)
        </button>
    );

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <ToastContainer position="top-right" autoClose={3000} />
            
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Notes Management</h1>
                <button
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg shadow hover:shadow-md transition-all"
                    onClick={() => setIsModalOpen(true)}
                >
                    <Plus size={18} />
                    Add Note
                </button>
            </div>

            {/* DataTable */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <DataTable
                    columns={columns}
                    data={notes}
                    progressPending={loading}
                    progressComponent={
                        <div className="p-4 text-center text-blue-500">Loading notes...</div>
                    }
                    noDataComponent={
                        <div className="p-4 text-center text-gray-500">
                            {error ? `Error: ${error}` : "No notes available"}
                        </div>
                    }
                    customStyles={customStyles}
                    selectableRows
                    selectableRowsHighlight
                    onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
                    contextActions={contextActions}
                    clearSelectedRows={toggleCleared}
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[5, 10, 15, 20]}
                    paginationComponentOptions={{
                        rowsPerPageText: 'Rows per page:',
                        rangeSeparatorText: 'of',
                        noRowsPerPage: false,
                        selectAllRowsItem: false,
                    }}
                    highlightOnHover
                    pointerOnHover
                />
            </div>

            {/* Add Note Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-xl font-bold text-gray-800">Add New Note</h3>
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="text-gray-400 hover:text-gray-500"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Title *
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Enter note title"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={newNote.title}
                                        onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Description *
                                    </label>
                                    <textarea
                                        placeholder="Enter note description"
                                        rows={4}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={newNote.description}
                                        onChange={(e) => setNewNote({ ...newNote, description: e.target.value })}
                                    />
                                </div>
                            </div>
                            
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                                    onClick={() => setIsModalOpen(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                    onClick={handleAddNote}
                                >
                                    <Save size={18} />
                                    Save Note
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}